import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { OrderService } from '../../../_services/order.service';
import { AlertService } from '../../../_services/alert.service';

export type OrderRange = 'today' | 'yesterday' | '7d' | '30d' | 'month';
export interface StatusTotal { status: string; count: number; value: number }

export const ORDER_RANGES: { key: OrderRange; label: string; long: string }[] = [
  { key: 'today', label: 'Today', long: 'today' },
  { key: 'yesterday', label: 'Yesterday', long: 'yesterday' },
  { key: '7d', label: '7 days', long: 'in the last 7 days' },
  { key: '30d', label: '30 days', long: 'in the last 30 days' },
  { key: 'month', label: 'This month', long: 'this month' },
];

// Colour per order status, shared with the "Today so far" links.
export function statusTone(s: string): { dot: string; pill: string } {
  if (s === 'COMPLETED') return { dot: 'bg-emerald-500', pill: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  if (s === 'PROCESSING') return { dot: 'bg-sky-500', pill: 'bg-sky-50 text-sky-700 border-sky-200' };
  if (s.startsWith('PENDING')) return { dot: 'bg-amber-400', pill: 'bg-amber-50 text-amber-800 border-amber-200' };
  if (s === 'DELAYED COMPLETION' || s === 'ON HOLD') return { dot: 'bg-orange-500', pill: 'bg-orange-50 text-orange-700 border-orange-200' };
  if (s === 'CANCELLED' || s === 'FAILED' || s === 'CHARGE BACK') return { dot: 'bg-rose-500', pill: 'bg-rose-50 text-rose-700 border-rose-200' };
  if (s.includes('REFUNDED')) return { dot: 'bg-violet-500', pill: 'bg-violet-50 text-violet-700 border-violet-200' };
  return { dot: 'bg-gray-400', pill: 'bg-gray-100 text-gray-600 border-gray-200' };
}

export function statusLabel(s: string): string {
  return s.charAt(0) + s.slice(1).toLowerCase();
}

interface StatusOrder {
  orderId: number;
  orderNo: string;
  orderDate: string;
  paymentDate: string | null;
  status: string;
  amount: number;
  paymentMethod: string;
  deliveryMethod: string | null;
  origin: string | null;
  postCode: string | null;
  customer: { id: number; name: string; guest?: boolean; company: string | null; email: string | null; phone: string | null };
  agent: string | null;
  itemCount: number;
  items: string[];
}

// Orders placed in a period with a given status, opened from the status
// links under "Today so far". Same slide-over layout as lost sales.
@Component({
    selector: 'app-order-activity',
    imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, MatProgressSpinnerModule],
    templateUrl: './order-activity.component.html'
})
export class OrderActivityComponent implements OnInit {
  @Input() status = 'ALL';
  @Input() range: OrderRange = 'today';
  @Output() closed = new EventEmitter<void>();

  readonly ranges = ORDER_RANGES;
  readonly tone = statusTone;
  readonly label = statusLabel;
  search = '';
  items: StatusOrder[] = [];
  count = 0;
  totals: StatusTotal[] = [];
  pageIndex = 0;
  readonly pageSize = 20;
  loading = true;
  loadingMore = false;
  loadError = false;
  private token = 0;
  private searchTimer: any;

  constructor(
    private orderService: OrderService,
    private alertService: AlertService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.load(true);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closed.emit();
  }

  get rangeLong(): string {
    return this.ranges.find((r) => r.key === this.range)?.long ?? '';
  }

  get allCount(): number {
    return this.totals.reduce((s, t) => s + t.count, 0);
  }

  get allValue(): number {
    return this.totals.reduce((s, t) => s + t.value, 0);
  }

  // Value of the status being shown (all of them for "All").
  get shownValue(): number {
    return this.status === 'ALL' ? this.allValue : this.totals.find((t) => t.status === this.status)?.value ?? 0;
  }

  // The tabs: every status with orders in the period, plus the one asked
  // for even if it has none.
  get tabs(): StatusTotal[] {
    const tabs = [...this.totals];
    if (this.status !== 'ALL' && !tabs.some((t) => t.status === this.status)) {
      tabs.push({ status: this.status, count: 0, value: 0 });
    }
    return tabs;
  }

  load(reset: boolean): void {
    const token = ++this.token;
    if (reset) {
      this.pageIndex = 0;
      this.loading = true;
    } else {
      this.loadingMore = true;
    }
    this.loadError = false;
    this.orderService
      .getOrdersByStatus({ status: this.status, range: this.range, search: this.search.trim(), pageSize: this.pageSize, pageIndex: this.pageIndex })
      .subscribe({
        next: (r: any) => {
          if (token !== this.token) return;
          this.items = reset ? r.items : [...this.items, ...r.items];
          this.count = r.count;
          this.totals = r.totals;
          this.loading = false;
          this.loadingMore = false;
        },
        error: (error) => {
          if (token !== this.token) return;
          this.loading = false;
          this.loadingMore = false;
          this.loadError = true;
          this.alertService.alert(error?.error?.message || 'Could not load orders', 'error');
        },
      });
  }

  loadMore(): void {
    this.pageIndex++;
    this.load(false);
  }

  get hasMore(): boolean {
    return this.items.length < this.count;
  }

  setStatus(s: string): void {
    if (this.status === s) return;
    this.status = s;
    this.load(true);
  }

  setRange(r: OrderRange): void {
    if (this.range === r) return;
    this.range = r;
    this.load(true);
  }

  onSearch(): void {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.load(true), 350);
  }

  // Time for today/yesterday, date and time further back.
  get whenFormat(): string {
    return this.range === 'today' || this.range === 'yesterday' ? 'HH:mm' : 'd MMM, HH:mm';
  }

  originLabel(origin: string | null): string {
    if (!origin) return '';
    const i = origin.indexOf('_cpc');
    return (i > 0 ? origin.substring(0, i) : origin).replace(/\.com$/i, '');
  }

  openOrder(o: StatusOrder): void {
    this.closed.emit();
    this.router.navigate(['/sales', o.orderNo]);
  }
}
